import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { ShoppingCart, Plus, Edit2, Trash2, Search } from 'lucide-react'
import { useInventoryStore } from '../store/inventoryStore'

export default function PurchaseOrdersPage() {
  const navigate = useNavigate()
  const purchaseOrders = useInventoryStore((state) => state.purchaseOrders)
  const addPurchaseOrder = useInventoryStore((state) => state.addPurchaseOrder)
  const updatePurchaseOrder = useInventoryStore((state) => state.updatePurchaseOrder)
  const deletePurchaseOrder = useInventoryStore((state) => state.deletePurchaseOrder)

  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [showAddModal, setShowAddModal] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [formData, setFormData] = useState({
    poNumber: '',
    supplier: '',
    expectedDelivery: '',
    status: 'pending',
    items: 0,
    totalValue: 0
  })

  const filteredOrders = useMemo(() => {
    return purchaseOrders.filter(order => {
      const matchesSearch = order.poNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           order.supplier.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesFilter = filterStatus === 'all' || order.status === filterStatus
      return matchesSearch && matchesFilter
    })
  }, [purchaseOrders, searchTerm, filterStatus])

  const handleAddOrder = () => {
    if (formData.poNumber && formData.supplier) {
      addPurchaseOrder(formData)
      setFormData({
        poNumber: '',
        supplier: '',
        expectedDelivery: '',
        status: 'pending',
        items: 0,
        totalValue: 0
      })
      setShowAddModal(false)
    }
  }

  const handleEditOrder = (order) => {
    setSelectedOrder(order.id)
    setFormData(order)
    setShowAddModal(true)
  }

  const handleUpdateOrder = () => {
    if (selectedOrder && formData.poNumber && formData.supplier) {
      updatePurchaseOrder(selectedOrder, formData)
      setFormData({
        poNumber: '',
        supplier: '',
        expectedDelivery: '',
        status: 'pending',
        items: 0,
        totalValue: 0
      })
      setSelectedOrder(null)
      setShowAddModal(false)
    }
  }

  const handleDeleteOrder = (id) => {
    if (confirm('Delete this purchase order?')) {
      deletePurchaseOrder(id)
    }
  }

  const closeModal = () => {
    setShowAddModal(false)
    setSelectedOrder(null)
    setFormData({
      poNumber: '',
      supplier: '',
      expectedDelivery: '',
      status: 'pending',
      items: 0,
      totalValue: 0
    })
  }

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return 'bg-yellow-500/20 text-yellow-300'
      case 'shipped': return 'bg-blue-500/20 text-blue-300'
      case 'delivered': return 'bg-green-500/20 text-green-300'
      default: return 'bg-slate-500/20 text-slate-300'
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
      <div className="space-y-6 p-8">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button onClick={() => navigate(-1)} className="text-blue-400 hover:text-blue-300">←</button>
            <h1 className="text-4xl font-bold text-white flex items-center space-x-3">
              <ShoppingCart className="w-8 h-8 text-indigo-400" />
              <span>Purchase Orders</span>
            </h1>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg transition-all"
          >
            <Plus className="w-5 h-5" />
            <span>Create Order</span>
          </button>
        </div>

        {/* Search & Filter */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 w-5 h-5 text-blue-400" />
            <input
              type="text"
              placeholder="Search by PO number or supplier..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-slate-800 border border-blue-500/20 rounded-lg pl-10 pr-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="bg-slate-800 border border-blue-500/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-400/50"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="shipped">Shipped</option>
            <option value="delivered">Delivered</option>
          </select>
        </div>

        {/* Orders Table */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-blue-500/20">
                <th className="text-left py-3 px-4 text-blue-300 font-semibold">PO Number</th>
                <th className="text-left py-3 px-4 text-blue-300 font-semibold">Supplier</th>
                <th className="text-left py-3 px-4 text-blue-300 font-semibold">Order Date</th>
                <th className="text-left py-3 px-4 text-blue-300 font-semibold">Expected Delivery</th>
                <th className="text-left py-3 px-4 text-blue-300 font-semibold">Items</th>
                <th className="text-left py-3 px-4 text-blue-300 font-semibold">Total Value</th>
                <th className="text-left py-3 px-4 text-blue-300 font-semibold">Status</th>
                <th className="text-left py-3 px-4 text-blue-300 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => (
                <tr key={order.id} className="border-b border-blue-500/10 hover:bg-blue-500/5 transition-all">
                  <td className="py-3 px-4 text-white font-mono font-bold">{order.poNumber}</td>
                  <td className="py-3 px-4 text-white">{order.supplier}</td>
                  <td className="py-3 px-4 text-blue-300 text-sm">{order.orderDate}</td>
                  <td className="py-3 px-4 text-blue-300 text-sm">{order.expectedDelivery}</td>
                  <td className="py-3 px-4 text-white">{order.items}</td>
                  <td className="py-3 px-4 text-green-400 font-semibold">${order.totalValue.toFixed(2)}</td>
                  <td className="py-3 px-4">
                    <span className={`text-xs font-bold px-3 py-1 rounded-full ${getStatusColor(order.status)}`}>
                      {order.status.toUpperCase()}
                    </span>
                  </td>
                  <td className="py-3 px-4 flex items-center space-x-2">
                    <button
                      onClick={() => handleEditOrder(order)}
                      className="text-blue-400 hover:text-blue-300 p-2 hover:bg-blue-500/10 rounded"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteOrder(order.id)}
                      className="text-red-400 hover:text-red-300 p-2 hover:bg-red-500/10 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Add/Edit Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-lg border border-blue-500/20 max-w-md w-full p-6">
              <h2 className="text-2xl font-bold text-white mb-4">
                {selectedOrder ? 'Edit Order' : 'Create Purchase Order'}
              </h2>
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="PO Number"
                  value={formData.poNumber}
                  onChange={(e) => setFormData({...formData, poNumber: e.target.value})}
                  className="w-full bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
                />
                <input
                  type="text"
                  placeholder="Supplier"
                  value={formData.supplier}
                  onChange={(e) => setFormData({...formData, supplier: e.target.value})}
                  className="w-full bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
                />
                <input
                  type="date"
                  value={formData.expectedDelivery}
                  onChange={(e) => setFormData({...formData, expectedDelivery: e.target.value})}
                  className="w-full bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
                />
                <input
                  type="number"
                  placeholder="Number of Items"
                  value={formData.items}
                  onChange={(e) => setFormData({...formData, items: parseInt(e.target.value)})}
                  className="w-full bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
                />
                <input
                  type="number"
                  placeholder="Total Value"
                  value={formData.totalValue}
                  onChange={(e) => setFormData({...formData, totalValue: parseFloat(e.target.value)})}
                  className="w-full bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
                />
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-400/50"
                >
                  <option value="pending">Pending</option>
                  <option value="shipped">Shipped</option>
                  <option value="delivered">Delivered</option>
                </select>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={closeModal}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={selectedOrder ? handleUpdateOrder : handleAddOrder}
                  className="flex-1 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg transition-all"
                >
                  {selectedOrder ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
